import { isChatStatusBusy, type Thread } from '@littlebird/sdk';
import { useEffect, useRef, useState } from 'react';

// Debounce delay before isBusy transitions from true→false.
// The backend can briefly set thread status to Done/NotSubmitted between
// reasoning steps (e.g., Thinking→Done→Streaming), causing isBusy to thrash.
// This delay absorbs those gaps so downstream effects (scroll-follow,
// turn-completion scroll, spacer cleanup) don't fire mid-turn.
const BUSY_LINGER_MS = 1000;

export function useChatState(thread?: Thread) {
  const rawBusy = isChatStatusBusy(thread?.status);

  const [isBusy, setIsBusy] = useState(rawBusy);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (rawBusy) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setIsBusy(true);
    } else if (!timerRef.current) {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setIsBusy(false);
      }, BUSY_LINGER_MS);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [rawBusy]);

  return { isBusy };
}
